import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useVideoStore } from '@stores/video';
import { IVideoData } from '@interfaces/video.interface';
import api from '@lib/api';

interface VideoContextFetchOptions {
  silent?: boolean;
}

function useVideoContext(videoId?: string) {
  const { video, setVideo, disposeVideo } = useVideoStore();
  const [loading, setLoading] = useState(video?.videoId === videoId ? false : true);
  const [error, setError] = useState<AxiosError<any, any> | null>(null);
  const controller = useRef(new AbortController());

  const fetch = useCallback(
    async (options?: VideoContextFetchOptions) => {
      if (!options?.silent) setLoading(true);
      setError(null);

      try {
        const res = await api.get<IVideoData>(`/video/${videoId}`, {
          params: {
            opt_fields: 'pose',
          },
          signal: controller.current.signal,
        });

        setVideo(res.data);
        setError(null);
        setLoading(false);
      } catch (e: unknown) {
        if (axios.isAxiosError(e)) setError(e);
        else {
          console.error(e);
        }
      }
    },
    [videoId, setVideo],
  );

  const cancel = () => {
    controller.current.abort();
  };

  useEffect(() => {
    if (!videoId || videoId === video?.videoId) return;

    disposeVideo();
    fetch();
  }, [video, videoId, disposeVideo, fetch]);

  return [
    {
      data: video,
      loading,
      error,
    },
    fetch,
    cancel,
  ] as const;
}

export default useVideoContext;
